import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useAuthContext } from 'contexts/AuthContext';
import './PumpDetailPanel.scss';
import { httpGetPumpDetails } from 'services/pumps';
import EditPumpModal from './EditPumpModal';
import PumpEventsModal from './PumpEventsModal';
import FilterablePoolSelect from './FilterablePoolSelect';
import {
  pumpStatusTranslationKey,
  PUMP_STATUS_MENU_ORDER,
  getSettablePumpStatusCodes,
} from './pumpStatusMeta';

export default function PumpDetailPanel({ pumpId, onBack }) {
  const { t, i18n } = useTranslation();
  const { userRole } = useAuthContext();
  const { updatePump: updatePumpInStore } = useStoreActions((actions) => actions.pumps);
  const { getPools } = useStoreActions((actions) => actions.pools);
  const { pools } = useStoreState((state) => state.pools);
  const pumpFromStore = useStoreState((state) =>
    state.pumps.pumps.find((p) => p.id === parseInt(pumpId, 10))
  );
  const [pump, setPump] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPool, setSelectedPool] = useState('');
  const [comment, setComment] = useState('');
  const [replacedParts, setReplacedParts] = useState('');
  const isKorjaaja = userRole === 3;
  const isKaivosKayttaja = userRole === 4;
  const inUseReadOnlyForRepairer = isKorjaaja && Number(pump?.status) === 2;

  const statusLabel = (code) => t(pumpStatusTranslationKey(code));
  const statusSelectOrder = getSettablePumpStatusCodes(userRole, pump?.status);

  const locale = i18n.language === 'en' ? 'en-GB' : 'fi-FI';

  useEffect(() => {
    if (pools.length === 0) {
      getPools();
    }
  }, [pools.length, getPools]);

  useEffect(() => {
    if (pump && pools.length > 0) {
      const poolIdVal =
        typeof pump.current_pool === 'object' ? pump.current_pool?.id : pump.current_pool;
      if (poolIdVal != null) {
        const poolExists = pools.some((p) => p.id === parseInt(poolIdVal, 10) || p.id === poolIdVal);
        if (poolExists) {
          setSelectedPool(String(poolIdVal));
        }
      }
    }
  }, [pump, pools]);

  useEffect(() => {
    if (!pumpId || !pumpFromStore || !pump) return;
    if (pump.id !== pumpFromStore.id) return;
    setPump((prev) => (prev ? { ...prev, ...pumpFromStore } : pumpFromStore));
    setSelectedStatus(pumpFromStore.status?.toString() ?? '');
    const poolIdVal =
      typeof pumpFromStore.current_pool === 'object'
        ? pumpFromStore.current_pool?.id
        : pumpFromStore.current_pool;
    setSelectedPool(poolIdVal != null ? String(poolIdVal) : '');
  }, [pumpId, pumpFromStore, pump?.id]);

  useEffect(() => {
    if (!pumpId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    httpGetPumpDetails(pumpId)
      .then((pumpResponse) => {
        const pumpData = pumpResponse.data;
        setPump(pumpData);
        setSelectedStatus(pumpData.status?.toString() || '');
        const poolIdVal =
          typeof pumpData.current_pool === 'object'
            ? pumpData.current_pool?.id
            : pumpData.current_pool;
        if (poolIdVal != null) {
          setSelectedPool(String(poolIdVal));
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading pump data:', err);
        setError(t('pump_detail_error_load'));
        setLoading(false);
      });
  }, [pumpId, t]);

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    if (e.target.value !== '2') {
      setSelectedPool('');
    }
    if (e.target.value !== '6') {
      setReplacedParts('');
    }
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
  };

  const savedPoolId = (() => {
    const poolIdVal = typeof pump?.current_pool === 'object' ? pump?.current_pool?.id : pump?.current_pool;
    return poolIdVal != null ? String(poolIdVal) : '';
  })();
  const statusChanged = String(selectedStatus) !== String(pump?.status ?? '');
  const poolChanged = String(selectedPool || '') !== savedPoolId;
  const commentText = comment.trim();
  const onlyComment = !statusChanged && !poolChanged && commentText.length > 0;
  const canSave = inUseReadOnlyForRepairer
    ? onlyComment
    : Boolean(selectedStatus) && (onlyComment || statusChanged || poolChanged);

  const handleSaveStatus = async () => {
    if (!onlyComment) {
      if (!selectedStatus) {
        setError(t('pump_detail_error_select_status'));
        return;
      }

      if (selectedStatus === '2' && !selectedPool) {
        setError(t('pump_detail_error_pool_required'));
        return;
      }
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updateData = onlyComment
        ? { comment: commentText }
        : {
            status: parseInt(selectedStatus, 10),
            ...(commentText && { comment: commentText }),
            ...(selectedStatus === '2' && selectedPool && { current_pool_id: parseInt(selectedPool, 10) }),
            ...(selectedStatus !== '2' && { current_pool_id: null }),
            ...(selectedStatus === '6' && replacedParts && { replaced_parts: replacedParts }),
          };

      const updatedPump = await updatePumpInStore({ id: pumpId, data: updateData });

      if (updatedPump) {
        setSuccess(t('pump_detail_success_save'));
        setPump(updatedPump);
        setSelectedStatus(updatedPump.status?.toString() || '');
        const poolIdVal =
          typeof updatedPump.current_pool === 'object'
            ? updatedPump.current_pool?.id
            : updatedPump.current_pool;
        if (poolIdVal != null) {
          setSelectedPool(String(poolIdVal));
        } else {
          setSelectedPool('');
        }
        setComment('');
        setReplacedParts('');
      } else {
        setError(t('pump_detail_error_save'));
      }
    } catch (err) {
      console.error('Error updating pump:', err);
      setError(t('pump_detail_error_save'));
    } finally {
      setSaving(false);
    }
  };

  const pumpData = useMemo(() => {
    if (!pump) return null;
    return {
      name: pump.name,
      serialNumber: pump.serial_number ?? pump.serialNumber ?? '',
      status: pump.status,
      model: pump.model,
      usage: pump.formatted_active_time || pump.usage || '',
      lastService: pump.last_maintenance_date
        ? new Date(pump.last_maintenance_date).toLocaleDateString(locale)
        : '',
      service: pump.last_maintenance_replaced_parts || pump.last_service || '',
      cycles: pump.rounds_count || pump.cycles || pump.maintenance_count || '',
      user: pump.last_maintenance_user_name || pump.user_name || '',
      logTime: pump.log_time || '',
      lift_height: pump.lift_height,
      volume_capacity: pump.volume_capacity,
      extendedRemovalRound: !!pump.spyro_round_policy,
      status_timestamps: pump.status_timestamps,
    };
  }, [pump, locale]);

  if (!pumpId) {
    return (
      <div className="pump-detail-panel">
        <div className="pump-detail-placeholder">
          <i className="fas fa-hand-pointer" style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }} />
          <h3>{t('pump_detail_placeholder_title')}</h3>
          <p>{t('pump_detail_placeholder_hint')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pump-detail-panel">
      <div className="details-header">
        {onBack && (
          <button type="button" className="back-btn" onClick={onBack}>
            ←
          </button>
        )}
        <h2 className="details-title">{t('pump_detail_title')}</h2>
        <button
          type="button"
          className="edit-btn"
          onClick={() => setShowEditModal(true)}
          disabled={loading || !pump}
          title={loading || !pump ? t('pump_detail_edit_loading') : t('pump_detail_edit_pump')}
        >
          <i className="fas fa-pen" />
        </button>
      </div>
      <div className="pump-details-content">
        {loading ? (
          <div className="text-center py-5">{t('ui_loading')}</div>
        ) : error && !pump ? (
          <div className="text-center py-5 text-danger">{error}</div>
        ) : pumpData ? (
          <>
            <div className="details-layout">
              <div className="details-left">
                <div className="details-table">
                  <div className="details-row details-status-inline">
                    <span className="details-label mb-0">{t('pump_detail_label_status')}</span>
                    <select
                      id="pump-detail-status-select"
                      className="form-select"
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      disabled={inUseReadOnlyForRepairer}
                    >
                      <option value="">{t('pump_detail_select_status')}</option>
                      {pumpData?.status != null &&
                        !statusSelectOrder.includes(Number(pumpData.status)) && (
                          <option value={String(pumpData.status)} disabled>
                            {statusLabel(pumpData.status)}
                            {t('pump_detail_status_current_suffix')}
                          </option>
                        )}
                      {statusSelectOrder.map((code) => (
                        <option key={code} value={String(code)}>
                          {statusLabel(code)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="details-row details-field-stack details-comment-below-status">
                    <label className="form-label" htmlFor="pump-detail-comment">
                      {t('pump_detail_comment_label')}{' '}
                      <span className="text-dashboard-primary pump-detail-comment-hint">
                        {t('pump_detail_comment_hint')}
                      </span>
                    </label>
                    <textarea
                      id="pump-detail-comment"
                      className="comment-box"
                      value={comment}
                      onChange={handleCommentChange}
                      placeholder={t('pump_detail_comment_placeholder')}
                      rows={4}
                    />
                  </div>
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_name')}</span> <span>{pumpData.name}</span>
                  </div>
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_serial')}</span>{' '}
                    <span>{pumpData.serialNumber || '-'}</span>
                  </div>
                  <div className="details-row details-status-inline">
                    <span className="details-label mb-0">{t('pump_detail_label_location')}</span>
                    <div className="details-inline-pool-wrap">
                      <FilterablePoolSelect
                        pools={pools}
                        value={selectedPool}
                        onChange={(poolId) => setSelectedPool(poolId?.toString() || '')}
                        placeholder={t('pool_search_by_name')}
                        emptyOptionLabel={t('pump_no_pool')}
                      />
                    </div>
                  </div>
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_lift_height')}</span> 
                    <span>{pumpData.lift_height != null ? `${pumpData.lift_height} m` : '-'}</span>
                  </div>
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_volume_capacity')}</span> 
                    <span>{pumpData.volume_capacity != null ? `${pumpData.volume_capacity} m³` : '-'}</span>
                  </div>
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_model')}</span> <span>{pumpData.model}</span>
                  </div>
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_usage')}</span> <span>{pumpData.usage}</span>
                  </div>
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_last_maint')}</span>{' '}
                    <span>{pumpData.lastService || t('pump_detail_unknown')}</span>
                  </div>
                  {pumpData.status_timestamps && Object.keys(pumpData.status_timestamps).length > 0 && (
                    <>
                      <div className="details-row details-section-label">
                        <span>{t('pump_detail_section_status_times')}</span>
                      </div>
                      {PUMP_STATUS_MENU_ORDER.map((code) => {
                        const ts = pumpData.status_timestamps[String(code)];
                        if (!ts) return null;
                        return (
                          <div key={code} className="details-row">
                            <span className="details-label">{statusLabel(code)}:</span>
                            <span>{new Date(ts).toLocaleString(locale)}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_service')}</span> <span>{pumpData.service || '-'}</span>
                  </div>
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_round')}</span> <span>{pumpData.cycles || '-'}</span>
                  </div>
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_extended')}</span>{' '}
                    <span>{pumpData.extendedRemovalRound ? t('pump_detail_yes') : t('pump_detail_no')}</span>
                  </div>
                  <div className="details-row details-description-row">
                    <span className="details-description">{t('pump_detail_extended_help_1')}</span>
                    <span className="details-description">{t('pump_detail_extended_help_2')}</span>
                  </div>
                  <div className="details-row">
                    <span className="details-label">{t('pump_detail_label_author')}</span> <span>{pumpData.user}</span>
                  </div>
                </div>

                <div className="details-actions">
                  <button
                    type="button"
                    className="show-log-btn"
                    onClick={() => setShowEventsModal(true)}
                    style={{ width: '100%' }}
                  >
                    {t('pump_detail_btn_log')}
                  </button>
                </div>

                <div className="details-save-footer">
                  {error && (
                    <div className="form-error details-save-footer-message">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="form-success details-save-footer-message">
                      {success}
                    </div>
                  )}

                  <button
                    type="button"
                    className="save-btn"
                    onClick={handleSaveStatus}
                    disabled={saving || !canSave}
                  >
                    {saving ? t('ui_saving') : t('Save')}
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>

      <EditPumpModal
        isOpen={showEditModal}
        pumpId={pumpId}
        initialData={pump}
        onClose={() => setShowEditModal(false)}
        onSuccess={(updatedPump) => {
          setShowEditModal(false);
          setPump(updatedPump);
        }}
      />

      <PumpEventsModal isOpen={showEventsModal} pumpId={pumpId} onClose={() => setShowEventsModal(false)} />
    </div>
  );
}
